import { useState, useCallback, useEffect } from 'react';
import { applyNodeChanges, applyEdgeChanges, addEdge } from '@xyflow/react';
import { toast } from 'react-toastify';
import automationApi from '../services/automationApi';
import { flowToRule, ruleToFlow } from '../components/FlowBuilder/utils/serializer';
import { NODE_TYPES, TRIGGER_TYPES, TRIGGER_TYPE_LABELS, DEFAULT_RULE_SETTINGS, OPERATORS } from '../constants';
import { createDefaultConditionGroup } from '../components/FlowBuilder/conditions/conditionUtils';

const getId = () => `node_${crypto.randomUUID()}`;

// Operators that don't require a comparison value
const VALUE_LESS_OPERATORS = [OPERATORS.IS_NULL, OPERATORS.IS_NOT_NULL];

/**
 * Validates all conditions in a group recursively.
 * Returns an error message string if invalid, or null if valid.
 */
const validateConditionGroup = (group) => {
  if (!group) return null;

  for (const c of group.conditions || []) {
    if (!c.field) {
      return 'A condition is missing a field';
    }
    if (!c.operator) {
      return `"${c.field}": operator is required`;
    }
    if (!VALUE_LESS_OPERATORS.includes(c.operator)) {
      if (c.value === undefined || c.value === null || c.value === '') {
        return `"${c.field}": value is required for "${c.operator.replace(/_/g, ' ')}"`;
      }
    }
  }

  for (const sub of group.groups || []) {
    const err = validateConditionGroup(sub);
    if (err) return err;
  }

  return null;
};

const useFlowBuilder = () => {
  const [nodes, setNodes] = useState([]);
  const [edges, setEdges] = useState([]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [ruleName, setRuleName] = useState('');
  const [ruleDescription, setRuleDescription] = useState('');
  const [ruleSettings, setRuleSettings] = useState(DEFAULT_RULE_SETTINGS);
  const [saving, setSaving] = useState(false);
  const [loadedRule, setLoadedRule] = useState(null);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [actionTypes, setActionTypes] = useState([]);
  const [fieldDefinitions, setFieldDefinitions] = useState([]);

  useEffect(() => {
    automationApi.getEventTypes()
      .then(data => setEventTypes(Array.isArray(data) ? data : data?.events || []))
      .catch(() => toast.error('Failed to load event types'));
    automationApi.getActionTypes()
      .then(data => setActionTypes(Array.isArray(data) ? data : data?.actions || []))
      .catch(() => toast.error('Failed to load action types'));
    automationApi.getFields()
      .then(data => setFieldDefinitions(Array.isArray(data) ? data : []))
      .catch(() => toast.error('Failed to load field definitions'));
  }, []);

  const onNodesChange = useCallback((changes) => {
    setNodes((nds) => applyNodeChanges(changes, nds));
  }, []);

  const onEdgesChange = useCallback((changes) => {
    setEdges(eds => applyEdgeChanges(changes, eds));
  }, []);

  const onConnect = useCallback((connection) => {
    setEdges(eds => addEdge({ ...connection, type: 'flow' }, eds));
  }, []);

  const addNode = useCallback((type, position, subtype) => {
    const id = getId();
    let data = {};

    if (type === NODE_TYPES.TRIGGER) {
      data = {
        triggerType: subtype || TRIGGER_TYPES.EVENT,
        eventName: '',
        schedule: '',
        webhookPath: '',
        webhookSecret: '',
        hasExistingSecret: false,
        label: TRIGGER_TYPE_LABELS[subtype] || 'Select a trigger',
      };
    } else if (type === NODE_TYPES.CONDITION) {
      data = { conditionGroup: createDefaultConditionGroup(), label: 'Set condition' };
    } else if (type === NODE_TYPES.ACTION) {
      data = { actionType: subtype || '', config: {}, onError: 'stop', label: subtype || 'Select an action' };
    }

    setNodes(nds => {
      // Only allow one trigger node on the canvas
      if (type === NODE_TYPES.TRIGGER && nds.some(n => n.type === NODE_TYPES.TRIGGER)) {
        toast.error('Only one trigger per rule is allowed');
        return nds;
      }
      return [...nds, { id, type, position, data }];
    });
  }, []);

  const updateNodeData = useCallback((nodeId, data) => {
    setNodes(nds => nds.map(n =>
      n.id === nodeId ? { ...n, data: { ...data } } : n
    ));
    setSelectedNode(prev => prev?.id === nodeId ? { ...prev, data: { ...data } } : prev);
  }, []);

  const deleteNode = useCallback((nodeId) => {
    setNodes(nds => nds.filter(n => n.id !== nodeId));
    setEdges(eds => eds.filter(e => e.source !== nodeId && e.target !== nodeId));
  }, []);

  const saveRule = useCallback(async (existingRuleId) => {
    // Validate condition nodes have complete field/operator/value
    const conditionNodes = nodes.filter(n => n.type === NODE_TYPES.CONDITION);
    for (const node of conditionNodes) {
      const err = validateConditionGroup(node.data?.conditionGroup);
      if (err) {
        toast.error(`Condition: ${err}`);
        return false;
      }
    }

    // Validate required config fields before saving
    const actionNodes = nodes.filter(n => n.type === NODE_TYPES.ACTION);
    for (const node of actionNodes) {
      const actionMeta = actionTypes.find(a => (a.type || a.value) === node.data?.actionType);
      const requiredFields = (actionMeta?.configSchema || []).filter(f => f.required);
      for (const field of requiredFields) {
        const val = node.data?.config?.[field.name];
        if (val === undefined || val === null || val === '') {
          toast.error(`"${node.data?.label || node.data?.actionType}": ${field.label || field.name} is required`);
          return false;
        }
      }
    }

    setSaving(true);
    try {
      const ruleData = flowToRule(nodes, edges, {
        name: ruleName,
        description: ruleDescription,
        settings: ruleSettings,
      });

      if (existingRuleId) {
        await automationApi.updateRule(existingRuleId, ruleData);
        toast.success('Rule updated');
      } else {
        await automationApi.createRule(ruleData);
        toast.success('Rule created');
      }
      return true;
    } catch (err) {
      toast.error(err.response?.body?.message || 'Failed to save rule');
      return false;
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, ruleName, ruleDescription, ruleSettings, actionTypes]);

  const saveAsNewRule = useCallback(async () => {
    setSaving(true);
    try {
      const ruleData = flowToRule(nodes, edges, {
        name: `${ruleName} (Copy)`,
        description: ruleDescription,
        settings: ruleSettings,
      });
      const newRule = await automationApi.createRule(ruleData);
      toast.success('Saved as new rule');
      return newRule;
    } catch (err) {
      toast.error(err.response?.body?.message || 'Failed to save as new rule');
      return null;
    } finally {
      setSaving(false);
    }
  }, [nodes, edges, ruleName, ruleDescription, ruleSettings]);

  const loadRule = useCallback(async (ruleId) => {
    try {
      const rule = await automationApi.getRule(ruleId);
      setLoadedRule(rule);
      setActiveVersionId(null);
      setRuleName(rule.name || '');
      setRuleDescription(rule.description || '');
      setRuleSettings(rule.settings || DEFAULT_RULE_SETTINGS);

      const { nodes: flowNodes, edges: flowEdges } = ruleToFlow(rule);
      setNodes(flowNodes);
      setEdges(flowEdges);
    } catch (err) {
      toast.error('Failed to load rule');
    }
  }, []);

  const loadVersion = useCallback((version) => {
    setActiveVersionId(version._id || version.version);
    setSelectedNode(null);

    const { nodes: flowNodes, edges: flowEdges } = ruleToFlow(version);
    setNodes(flowNodes);
    setEdges(flowEdges);
    setRuleName(version.name || '');
    setRuleDescription(version.description || '');
    setRuleSettings(version.settings || DEFAULT_RULE_SETTINGS);
  }, []);

  const restoreCurrentVersion = useCallback(() => {
    if (!loadedRule) return;
    setActiveVersionId(null);
    setSelectedNode(null);

    const { nodes: flowNodes, edges: flowEdges } = ruleToFlow(loadedRule);
    setNodes(flowNodes);
    setEdges(flowEdges);
    setRuleName(loadedRule.name || '');
    setRuleDescription(loadedRule.description || '');
    setRuleSettings(loadedRule.settings || DEFAULT_RULE_SETTINGS);
  }, [loadedRule]);

  return {
    nodes,
    edges,
    selectedNode,
    ruleName,
    ruleDescription,
    ruleSettings,
    eventTypes,
    actionTypes,
    fieldDefinitions,
    saving,
    loadedRule,
    setRuleName,
    setRuleDescription,
    setRuleSettings,
    setSelectedNode,
    onNodesChange,
    onEdgesChange,
    onConnect,
    addNode,
    updateNodeData,
    deleteNode,
    saveRule,
    saveAsNewRule,
    loadRule,
    loadVersion,
    restoreCurrentVersion,
    activeVersionId,
  };
};

export default useFlowBuilder;
